import React, { useState, useEffect } from "react";
import {  useParams } from "react-router-dom";
import Loader from "../../Layout/Loader";
import mainAxios from "../../../services/mainAxios";
import { useTranslation } from "react-i18next";
import { useAppContext, usePersistedState } from "../../../AppContext";
import PrimeTable from "../../PrimeTable/PrimeTable";
import BankJournalExportMenu from "./BankJournalExportMenu";
import DateRange from "../../global/DateRange";
import moment from "moment";
import { customStyles } from "../../../design/selectStyle";
import { useBankJournalColumns } from "./BankJournalColumns";
import ReactSelect from "react-select";
import { globalHandleChange, showToast, toastStatuses } from "../../../services/functions";
import { useOpenBanking } from "../UseOpenBanking";
import { useHistory } from 'react-router-dom';
import { Column } from "primereact/column";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import TransactionSyncInfo from "./TransactionSyncInfo";
import TooltipOverlay from "../../global/TooltipOverlay";



const BankJournals = () => {
  const {
    selectedCompany: [selectedCompany],
    dates: [dates, setDates],
    user: [user]
  } = useAppContext();
  const { t } = useTranslation("translations");
  const { accountNo } = useParams();
  const [transactions, setTransactions] = useState(null);
  const [transactionsForCsv, setTransactionsForCsv] = useState([]);
  const [loading, setLoading] = useState(false);
  const columns = useBankJournalColumns({transactions});
  const { loading:bankAccountsLoading,bankAccounts} = useOpenBanking()
  const history= useHistory()
  const [expandedRows,setExpandedRows] = useState([]);
  const [loadingPulse,setLoadingPulse] = useState(false)
  const [refresh,setRefresh]= useState(false)

  const filterParams = {
    accountNumber: accountNo ?? null, 
  };

  const defaultFilters = {
    fromDate: dates.fromDate,
    toDate: dates.toDate,
    params: {
      ...filterParams,
    },
  };

  const [filters, setFilters] = useState(defaultFilters);

  const defaultSortCriteria = {
    field: "bookingDate",
    direction: "DESC",
  };

  const defaultTableRows={
    totalRows: 0,
    rowsPerPage: 10,
    currentPage: 1,
    sortCriteria: defaultSortCriteria,
  }

  const [tableRows, setTableRows] = useState(defaultTableRows);

  const handleParamsChange = (e, label, val) => {
    const key = e ? e.target.name : label;
    const value = e ? e.target.value : val;
    setFilters((filters) => ({
      ...filters,
      params: {
        ...filters.params,
        [key]: value,
      },
    }));
  };

  React.useEffect(() => {
    getFilteredResults({ data: filters });
  }, [selectedCompany, accountNo,filters?.params?.accountNumber]);

  const getFilteredResults = ({
    data,
    pageSize = 10,
    page = 1,
    clearFilters = false,
    getTransactionsForCsv = false,
    sortCriteria = [defaultSortCriteria],
  } = {}) => {
    const requestFilters = data ? data : filters;

    if (!filters?.params?.accountNumber) {
      setTransactions([]);
      return Promise.resolve(false);
    }

    if (!requestFilters.fromDate || !requestFilters.toDate) {
      return Promise.resolve(false);
    }

    if (!getTransactionsForCsv) setLoading(true);
    if (transactionsForCsv.length > 0) setTransactionsForCsv([]);

    let requestBody = {
      object: "GetTransactions",
      fromDate: moment(requestFilters.fromDate).format("YYYY-MM-DD"),
      toDate: moment(requestFilters.toDate).format("YYYY-MM-DD"),
      ...requestFilters,
      params: {
        ...requestFilters.params,
      },
      pagination: getTransactionsForCsv
        ? null
        : {
            pageSize,
            pageNumber: page,
          },
      sortCriteria,
      username: user.username,
    };

    return mainAxios
      .post("/transactions/search", requestBody)
      .then((res) => {
        setLoading(false);
        if (res?.data?.status === true) {
          if (getTransactionsForCsv) {
            setTransactionsForCsv(res.data.content);
          } else {
            setTransactions(res.data.content);
            setTableRows((tableRows) => ({
              ...tableRows,
              totalRows: res?.data?.totalSize,
            }));
          }
          return true;
        }
        return false;
      })
      .catch((err) => {
        setLoading(false);
        return false;
      });
  };

  const [selectedColumns, setSelectedColumns] = usePersistedState(
    columns.map((col) => ({ field: col.field, show: true })),
    "transactionColumns1"
  );



  const exportTransactions = (
    <BankJournalExportMenu
      data={transactionsForCsv || []}
      selectedColumns={selectedColumns}
      columns={columns}
      accountNumber={filters?.params?.accountNumber}
      onClick={() => getFilteredResults({ getTransactionsForCsv: true })}
      excludedColumns={["actions"]}
    />
  );

  const bankAccountsComponent = (
    <div style={{ width: "500px" }}>
      <label className="w-100">Bank Account:</label>
      <ReactSelect
        styles={customStyles}
        value={bankAccounts.find(account => account.accountNo === filters?.params?.accountNumber) || null}
        options={bankAccounts?? []}
        onChange={(opt) => {
          handleParamsChange(null, "accountNumber", opt?.accountNo);
          setTableRows(defaultTableRows)
          history.replace(`/bank-journal${opt?.accountNo ? `/${opt.accountNo}` : ""}`);
        }}
        getOptionLabel={(opt) =>`${opt?.financialInstitution?.name}-${opt.iban}-${opt.currency}`}
        getOptionValue={(opt) => opt.accountNo}
        isLoading={bankAccountsLoading}
      />
    </div>
  )

    const showErrorMessage = (res) => {
    const error = res?.response?.data?.error ?? {}
    const errorCode =  error.errorCode
    const errorDescription = error?.errorDescription || "An unexpected error occurred"
    const nextAction = res?.data?.content?.[0]?.nextAction 
    const message = [
      `Code: ${errorCode}`,
      `Description: ${errorDescription}`,
      ...(nextAction ? [`Next Action: ${nextAction}`] : []),

    ].join("\n");

    showToast(message, res?.response?.data?.messageType ?? toastStatuses.ERROR);

  };

  const showSuccessMessage = (res) => {
    const content = res.data?.content?.[0]
    const message= [
      content.statusMessage,  
      ...(content?.nextAction ? [`Next Action: ${content?.nextAction}`] : []),
    ].join("\n")
    console.log({messageType:res?.data?.messageType })
    showToast(message || "Account pulse fetched successfully",res?.data?.messageType ?? toastStatuses.SUCCESS);

  };

  const getPulseOfAccounts = ()=>{
    if(!filters?.params?.accountNumber){
      toast.error("You should choose a Bank Account", {
        containerId: "all_toast",
      });
      return;
    }
    mainAxios.post("/transactions/fetch",{
      accountNumber: filters?.params?.accountNumber,
      companyId: parseInt(selectedCompany)
    },{showErrorToast:false})
    .then(res=>{
        setRefresh((prev)=>!prev)
        setLoadingPulse(false)
        if(res?.data?.status === true){
          getFilteredResults({
            pageSize: tableRows.rowsPerPage,
            page: tableRows.currentPage,
          });
          showSuccessMessage(res)
        } 
    }).catch(err=>{
        setLoadingPulse(false)
        showErrorMessage(err);
    })
  }

  const headerButtonsRight = (
    <button className="btn btn-outline-primary btn-sm mr-3" onClick={()=>getPulseOfAccounts()}>
        {loadingPulse ? (
            <Loader size="xs" color="white" />
          ) : (
            <>
              <FontAwesomeIcon icon={faUpload} className="mr-2" />
              Get Pulse of Accounts
            </>
          )}
      </button>
  )


const expandedColumns = [
    {
      header: "Entry Reference",
      field: 'transactionDetails.entryReference',
      style: { minWidth: "150px", maxWidth: "200px" },
    },
    {
      header: "Batch Indicator",
      field: 'transactionDetails.batchIndicator',
      style: { minWidth: "100px", maxWidth: "150px" },
      dataType: "boolean",
    },
    {
      header: "Batch No Of Transactions",
      field: 'transactionDetails.batchNumberOfTransactions',
      style: { minWidth: "150px", maxWidth: "200px" },
    },
    {
      header: "End To End Id",
      field: 'transactionDetails.endToEndId',
      style: { minWidth: "150px", maxWidth: "200px" },
    },
    {
      header: "Mandate Id",
      field: 'transactionDetails.mandateId',
      style: { minWidth: "150px", maxWidth: "200px" },
    },
    {
      header: "Check Id",
      field: 'transactionDetails.checkId',
      style: { minWidth: "150px", maxWidth: "200px" },
    },
    {
      header: "Currency Exchange",
      field: 'transactionDetails.currencyExchange',
      style: { minWidth: "150px", maxWidth: "200px" },
    },
    {
      header: "Purpose Code",
      field: 'transactionDetails.purposeCode',
      style: { minWidth: "100px", maxWidth: "150px" },
    },
    {
      header: "Bank Transaction Code",
      field: 'transactionDetails.bankTransactionCode',
      style: { minWidth: "150px", maxWidth: "200px" },
    },
  ]

  const onRowExpand = (e)=>{
    console.log({event:e})
  }
  const onRowCollapse = (e)=>{
    console.log({event:e})
  }

  const rowExpansionTemplate = (data) => {
    console.log({data})
    return (
      <div className="d-flex flex-column ml-3 my-3">
        <div className="d-flex align-items-center">
          <h4 className="my-1">Transaction Details</h4>
        </div>
          <PrimeTable
            value={[data]}
            columns={expandedColumns}
            showFilters={false}
            filterDisplay={false}
            responsiveLayout="scroll"
            showHeader={false}
            simpleTable={true}
            paginator={false}
            style={{maxWidth:"100vw"}}
          >
            <Column style={{width:40}}/>
          </PrimeTable>
      </div>
    )
  }

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <h3 className="mt-4 mb-4">Bank Journal</h3>
          <TransactionSyncInfo accountNumber={filters?.params?.accountNumber} refresh={refresh} />
        </div>
        <div className="mb-4 mb-md-0">
          <DateRange
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            onChange={(dates) => {
              let fromDate = dates[0];
              let toDate = dates[1];
              if (fromDate && toDate) {
                getFilteredResults({
                  data: {
                    fromDate,
                    toDate,
                    params: { ...filters.params },
                  },
                });
              }
              setFilters((filters) => ({
                ...filters,
                fromDate,
                toDate,
              }));
              setDates({ fromDate, toDate });
            }}
          />
        </div>
      </div>
          <PrimeTable
              columns={columns}
              loading={loading}
              value={transactions ?? []}
              filters={[filters, setFilters]}
              defaultFilters={defaultFilters}
              tableRows={[tableRows, setTableRows]}
              reorderableColumns={true}
              showFilters={true}
              filterDisplay={'row'}
              showSelectColumns={true}
              gridKey="transactionColumns1"
              selectedColumns={[selectedColumns,setSelectedColumns]}
              headerButtonsLeft={bankAccountsComponent}
              headerButtonsRight={headerButtonsRight}
              headerIcons={exportTransactions}
              style={{minHeight: "600px"}}
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              onRowExpand={onRowExpand}
              onRowCollapse={onRowCollapse}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="id"
              mobileExpansionLabel="Transactions"
              columnDeps={[t]}
              onRemoteDataChange={(remoteData) => {
                const updatedFilters = remoteData.filters ?? filters;
                const updatedTableData = remoteData.tableRows ?? tableRows;
                let { currentPage, rowsPerPage, sortCriteria } = updatedTableData;
                sortCriteria = sortCriteria ? [sortCriteria] : [];
                getFilteredResults({
                  data: updatedFilters,
                  pageSize: rowsPerPage,
                  page: currentPage,
                  sortCriteria,
                });
              }}
              defaultSortCriteria={defaultSortCriteria}
          >
          <Column  
          style={{ width: '40px' }} 
          frozen
          body={(transaction)=>{
            const noOfInvocies= transaction?.transactionDetails?.batchNumberOfTransactions
            return (
              <div className="d-flex justify-content-center align-items-center">
                {noOfInvocies>1 && (
                  <TooltipOverlay show={0} hide={0} title={t("openBanking.noTransactionsDesc",{noOfInvocies})}>
                    <div div className="round-badge ml-3" >{noOfInvocies}</div>
                  </TooltipOverlay>
                  )}
              </div>
            )
          }}
          />
        </PrimeTable>
    </>
  );
};

export default BankJournals;
